import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  BarChart3,
  BookOpen,
  Bot,
  Building2,
  CheckCircle2,
  Globe,
  LineChart,
  MapPinned,
  Menu,
  MessageSquareHeart,
  Network,
  Printer,
  QrCode,
  Search,
  ShieldCheck,
  Sparkles,
  Store,
  X,
} from "lucide-react";
import logoOuraTrust from "@/assets/logo-ouratrust-v2.png";
import dashboardScreenshot from "@/assets/dashboard-screenshot.jpg";
import { useLanguage } from "@/hooks/useLanguage";
import { Footer } from "@/components/Footer";
import { SocialProofToast } from "@/components/SocialProofToast";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { TrustBar } from "@/components/TrustBar";
import { PricingSection } from "@/components/landing/PricingSection";
import { TestimonialsSection } from "@/components/landing/TestimonialsSection";
import { SEOHead } from "@/components/SEOHead";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const Index = () => {
  const { t, language } = useLanguage();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const content = {
    fr: {
      hero: {
        eyebrow: "Cockpit de visibilité locale & réputation",
        title: "Comprenez, pilotez et améliorez votre présence locale.",
        subtitle:
          "OuraTrust réunit vos avis, votre fiche Google Business Profile, votre visibilité locale et vos concurrents dans une plateforme premium pensée pour les établissements qui veulent agir plus vite, plus clairement et avec plus de sérieux.",
        primaryCta: "Démarrer avec Pro",
        secondaryCta: "Voir la démo",
        support: "Essai gratuit 14 jours • Sans engagement • Mise en route simple",
        trustSignals: ["Réputation & avis", "Audit GBP", "Visibilité locale", "Concurrents locaux"],
        imageAlt: "Aperçu du cockpit OuraTrust combinant réputation, fiche Google et visibilité locale",
      },
      proof: {
        eyebrow: "Valeur immédiate",
        title: "Une seule plateforme pour ce que voit vraiment un client local",
        subtitle:
          "Au lieu d'empiler des outils séparés, OuraTrust structure deux piliers complémentaires dans une expérience claire : réputation & avis, puis visibilité locale.",
        items: [
          {
            title: "Réputation & avis",
            description: "Centralisez les plateformes, répondez plus vite avec l'IA et suivez les signaux qui influencent la confiance.",
          },
          {
            title: "Visibilité locale",
            description: "Analysez votre fiche Google Business Profile, votre présence locale standardisée et votre environnement concurrentiel.",
          },
          {
            title: "Collecte & feedback",
            description: "Activez la collecte via QR code, supports imprimables et feedback privé avant que l'insatisfaction ne sorte publiquement.",
          },
        ],
      },
      why: {
        eyebrow: "Pourquoi OuraTrust",
        title: "Une promesse forte, premium et crédible",
        subtitle:
          "OuraTrust n'essaie pas de vous vendre un SEO magique. La plateforme vous aide à comprendre votre présence locale, à protéger votre réputation et à agir avec des priorités concrètes.",
        items: [
          {
            title: "Une lecture locale plus complète",
            description: "Avis, fiche Google, visibilité standardisée et concurrents locaux sont enfin lus comme un ensemble cohérent.",
          },
          {
            title: "Des actions plus simples à arbitrer",
            description: "Vous voyez quoi corriger, quoi publier, quoi améliorer et où l'écart de présence est le plus significatif.",
          },
          {
            title: "Une montée en gamme naturelle",
            description: "Starter couvre la réputation. Pro ajoute la brique visibilité locale. Business renforce le multi-établissements et l'analyse.",
          },
        ],
      },
      visibility: {
        eyebrow: "Pilier 1",
        title: "Visibilité locale",
        subtitle:
          "Comprenez comment votre établissement se présente localement, ce qui affaiblit votre fiche et comment votre zone concurrentielle se structure autour de vous.",
        note:
          "Mesure standardisée selon les sources disponibles. OuraTrust ne promet pas un classement Google absolu : il vous donne un pilotage local plus fiable et plus actionnable.",
        features: [
          {
            title: "Audit GBP",
            description: "Passez votre fiche Google Business Profile au crible pour identifier les manques, les incohérences et les priorités d'optimisation.",
            bullets: ["Complétude de fiche", "Clarté des recommandations", "Vision exploitable rapidement"],
          },
          {
            title: "Visibilité locale",
            description: "Suivez une lecture standardisée de votre présence sur des mots-clés locaux sans survendre une précision que la source ne garantit pas toujours.",
            bullets: ["Mesures standardisées", "Historique lisible", "États de données plus honnêtes"],
          },
          {
            title: "Concurrents locaux",
            description: "Identifiez les établissements réellement proches et comprenez où se situent les écarts de présence, de densité et de profil.",
            bullets: ["Zone concurrentielle locale", "Lecture métier + géographie", "Décisions plus pertinentes"],
          },
        ],
      },
      reputation: {
        eyebrow: "Pilier 2",
        title: "Réputation & avis",
        subtitle:
          "Gardez le contrôle sur ce que vos clients disent, sur la vitesse de réponse et sur la qualité de votre présence multi-plateforme.",
        features: [
          {
            title: "Centralisation multi-plateforme",
            description: "Google, Facebook, Yelp, TripAdvisor et Trustpilot regroupés dans une seule interface claire.",
          },
          {
            title: "Réponses IA utiles et contrôlées",
            description: "Générez des brouillons plus vite, gardez la validation humaine et gagnez un temps réel sur la production de réponses.",
          },
          {
            title: "Analytics réputation",
            description: "Suivez l'évolution de vos notes, de vos volumes et des signaux d'insatisfaction avant qu'ils ne pèsent sur la confiance.",
          },
        ],
      },
      collection: {
        eyebrow: "Collecte & expérience client",
        title: "Faites remonter plus de feedback utile, sans complexifier le parcours",
        subtitle:
          "Le terrain compte autant que le dashboard : OuraTrust transforme un moment simple en point de collecte propre, premium et cohérent.",
        features: [
          {
            title: "QR code intelligent",
            description: "Dirigez les clients vers la bonne plateforme ou vers un feedback privé selon le parcours prévu.",
          },
          {
            title: "Supports prêts à imprimer",
            description: "Table tents, cartes et supports comptoir pour déployer la collecte sans bricolage visuel.",
          },
          {
            title: "Feedback privé avant avis public",
            description: "Captez des signaux utiles en amont pour traiter certaines insatisfactions avant qu'elles ne deviennent publiques.",
          },
        ],
      },
      workflow: {
        eyebrow: "Comment ça marche",
        title: "Un flow simple pour connecter, analyser et agir",
        subtitle:
          "Pas de mille réglages. Vous connectez vos sources, OuraTrust structure la lecture et vous aide à agir plus vite.",
        steps: [
          {
            title: "Connectez vos sources",
            description: "Reliez votre fiche Google Business Profile et vos plateformes d'avis dans un cockpit unique.",
          },
          {
            title: "Analysez votre présence locale",
            description: "Lisez l'audit GBP, la visibilité locale et les concurrents pour comprendre vos priorités réelles.",
          },
          {
            title: "Suivez réputation et avis",
            description: "Centralisez les retours, générez des réponses IA et gardez une vision claire des signaux critiques.",
          },
          {
            title: "Agissez plus vite",
            description: "Déployez la collecte QR, améliorez la fiche, arbitrez vos actions et montez en gamme si votre périmètre grandit.",
          },
        ],
      },
      useCases: {
        eyebrow: "Pour qui c'est fait",
        title: "Une plateforme pensée pour la maturité locale réelle d'un établissement",
        subtitle:
          "OuraTrust fonctionne autant pour un point de vente unique que pour un réseau qui veut industrialiser sa lecture locale.",
        items: [
          {
            title: "Établissement local",
            plan: "Starter",
            description: "Pour reprendre le contrôle des avis, accélérer les réponses et activer une collecte propre sans entrer tout de suite dans une brique SEO locale complète.",
          },
          {
            title: "Commerce ambitieux",
            plan: "Pro",
            description: "Pour ceux qui veulent gérer réputation + visibilité locale dans une même plateforme et comprendre enfin leur présence sur la zone.",
          },
          {
            title: "Réseau multi-établissements",
            plan: "Business",
            description: "Pour piloter plusieurs établissements, conserver plus d'historique et structurer une lecture premium plus avancée.",
          },
        ],
      },
      faq: {
        title: "Questions fréquentes",
        subtitle: "Une landing forte doit aussi répondre clairement aux vraies objections.",
        items: [
          {
            question: "OuraTrust est-il un outil de gestion d'avis ou une plateforme de visibilité locale ?",
            answer:
              "Les deux. OuraTrust combine un pilier réputation & avis et un pilier visibilité locale pour donner une lecture plus complète de la présence d'un établissement.",
          },
          {
            question: "Qu'est-ce qui est inclus dans Starter ?",
            answer:
              "Starter couvre la gestion d'avis et de réputation : centralisation, réponses IA, QR code, collecte et tableau de bord réputation. La brique complète de visibilité locale n'est pas incluse dans ce plan.",
          },
          {
            question: "Qu'ajoute Pro par rapport à Starter ?",
            answer:
              "Pro ajoute la suite visibilité locale : Audit GBP, Visibilité locale et Concurrents locaux, en plus de la couche réputation & avis.",
          },
          {
            question: "La visibilité locale correspond-elle à un classement Google exact ?",
            answer:
              "Non. OuraTrust adopte une approche honnête : il s'agit d'une mesure standardisée de présence locale, dépendante de la disponibilité et de la qualité des sources. L'objectif est un pilotage crédible, pas une promesse trompeuse.",
          },
          {
            question: "Quelles plateformes d'avis sont prises en charge ?",
            answer:
              "OuraTrust centralise aujourd'hui Google, Facebook, Yelp, TripAdvisor et Trustpilot afin de regrouper la réputation multi-plateforme dans une seule interface.",
          },
          {
            question: "L'IA répond-elle automatiquement à tous les avis ?",
            answer:
              "OuraTrust privilégie une IA utile mais contrôlée. La plateforme génère des brouillons pour vous faire gagner du temps, tout en gardant la validation humaine comme cadre de confiance principal.",
          },
          {
            question: "Comment gérez-vous la fiabilité des données SEO locales ?",
            answer:
              "Nous différencions les mesures réelles, les données indisponibles et l'historique afin d'éviter de présenter comme certain ce qui ne l'est pas. Le module vise la clarté d'interprétation avant tout.",
          },
          {
            question: "Business est-il pensé pour le multi-établissements ?",
            answer:
              "Oui. Business est conçu pour les réseaux, groupes ou enseignes qui veulent un cockpit centralisé, davantage d'historique et une lecture plus avancée de plusieurs établissements.",
          },
        ],
      },
      finalCta: {
        title: "Donnez à votre établissement un vrai cockpit local",
        subtitle:
          "Commencez par la réputation avec Starter, passez à Pro pour activer la visibilité locale, ou structurez plusieurs sites avec Business.",
        primaryCta: "Créer mon compte",
        secondaryCta: "Comparer les offres",
      },
    },
    en: {
      hero: {
        eyebrow: "Local visibility & reputation cockpit",
        title: "Understand, manage and improve your local presence.",
        subtitle:
          "OuraTrust brings together your reviews, your Google Business Profile, your local visibility and your local competitors in one premium platform built for businesses that want faster action, clearer priorities and stronger local control.",
        primaryCta: "Start with Pro",
        secondaryCta: "Watch demo",
        support: "14-day free trial • No commitment • Fast onboarding",
        trustSignals: ["Reputation & reviews", "GBP Audit", "Local visibility", "Local competitors"],
        imageAlt: "OuraTrust cockpit preview combining reputation, Google Business Profile and local visibility",
      },
      proof: {
        eyebrow: "Immediate value",
        title: "One platform for what a local customer actually sees",
        subtitle:
          "Instead of stacking disconnected tools, OuraTrust structures two complementary pillars in one clear experience: reputation & reviews, then local visibility.",
        items: [
          {
            title: "Reputation & reviews",
            description: "Centralize review platforms, respond faster with AI drafts and monitor the signals that shape trust.",
          },
          {
            title: "Local visibility",
            description: "Analyze your Google Business Profile, your standardized local visibility and the competitors around your location.",
          },
          {
            title: "Collection & feedback",
            description: "Activate QR-based collection, printable supports and private feedback before dissatisfaction turns public.",
          },
        ],
      },
      why: {
        eyebrow: "Why OuraTrust",
        title: "A stronger promise that stays credible",
        subtitle:
          "OuraTrust does not sell magical SEO. It helps you understand local presence, protect reputation and act on concrete priorities with a clearer product frame.",
        items: [
          {
            title: "A fuller local picture",
            description: "Reviews, Google Business Profile, standardized local visibility and nearby competitors are finally read as one coherent system.",
          },
          {
            title: "Clearer decisions",
            description: "You can see what to fix, what to publish, what to improve and where the biggest local gap really is.",
          },
          {
            title: "A natural upgrade path",
            description: "Starter covers reputation. Pro adds the full local visibility suite. Business expands the model for multi-location teams.",
          },
        ],
      },
      visibility: {
        eyebrow: "Pillar 1",
        title: "Local visibility",
        subtitle:
          "Understand how your business shows up locally, what weakens your profile and how your nearby competitive landscape is structured around you.",
        note:
          "Standardized measurement based on available sources. OuraTrust does not promise an absolute Google ranking: it gives you a more credible and more actionable local operating view.",
        features: [
          {
            title: "GBP Audit",
            description: "Review your Google Business Profile to uncover missing elements, inconsistencies and the most important optimization priorities.",
            bullets: ["Profile completeness", "Clear recommendations", "Fast business interpretation"],
          },
          {
            title: "Local visibility",
            description: "Track a standardized reading of your local keyword presence without overselling a level of precision the source cannot always guarantee.",
            bullets: ["Standardized measures", "Readable history", "More honest data states"],
          },
          {
            title: "Local competitors",
            description: "Identify truly nearby competitors and understand the visibility, density and profile gaps that matter in your area.",
            bullets: ["Local competitive zone", "Business + geography logic", "Better prioritization"],
          },
        ],
      },
      reputation: {
        eyebrow: "Pillar 2",
        title: "Reputation & reviews",
        subtitle:
          "Stay in control of what customers say, how fast you respond and how your multi-platform reputation evolves over time.",
        features: [
          {
            title: "Multi-platform centralization",
            description: "Google, Facebook, Yelp, TripAdvisor and Trustpilot combined into one clear operating interface.",
          },
          {
            title: "Useful, controlled AI responses",
            description: "Generate stronger drafts faster, keep human validation and save real time on reply production.",
          },
          {
            title: "Reputation analytics",
            description: "Monitor rating shifts, volume changes and dissatisfaction signals before they start hurting trust.",
          },
        ],
      },
      collection: {
        eyebrow: "Collection & customer experience",
        title: "Capture more useful feedback without adding friction",
        subtitle:
          "The field experience matters as much as the dashboard: OuraTrust turns simple customer moments into a premium, structured collection flow.",
        features: [
          {
            title: "Smart QR code",
            description: "Route customers to the right platform or to private feedback depending on the journey you want to create.",
          },
          {
            title: "Print-ready supports",
            description: "Table tents, cards and counter materials to deploy collection without improvised visuals.",
          },
          {
            title: "Private feedback before public reviews",
            description: "Catch useful signals earlier and resolve some issues before they become public reputation events.",
          },
        ],
      },
      workflow: {
        eyebrow: "How it works",
        title: "A simple flow to connect, analyze and act",
        subtitle:
          "No bloated setup. You connect your sources, OuraTrust structures the signal and helps you move faster with better priorities.",
        steps: [
          {
            title: "Connect your sources",
            description: "Link your Google Business Profile and review platforms into one operating cockpit.",
          },
          {
            title: "Analyze local presence",
            description: "Read the GBP audit, local visibility and competitor view to understand real local priorities.",
          },
          {
            title: "Track reviews and reputation",
            description: "Centralize customer feedback, generate AI-assisted replies and keep a clear read on critical signals.",
          },
          {
            title: "Act faster",
            description: "Deploy QR collection, improve the profile, prioritize actions and upgrade your plan as your scope grows.",
          },
        ],
      },
      useCases: {
        eyebrow: "Built for",
        title: "Designed for the real local maturity of a business",
        subtitle:
          "OuraTrust works for a single location, an ambitious local brand or a network that wants stronger local operating discipline.",
        items: [
          {
            title: "Single-location business",
            plan: "Starter",
            description: "For teams that want to regain control of reviews, speed up replies and launch proper QR collection without committing yet to the full local visibility suite.",
          },
          {
            title: "Ambitious local business",
            plan: "Pro",
            description: "For businesses that want reputation + local visibility in one platform and a clearer view of how they show up across their area.",
          },
          {
            title: "Multi-location network",
            plan: "Business",
            description: "For groups that need centralized oversight, deeper history and a more advanced premium layer for several locations.",
          },
        ],
      },
      faq: {
        title: "Frequently asked questions",
        subtitle: "A strong landing page should answer the real objections with clarity.",
        items: [
          {
            question: "Is OuraTrust a review management tool or a local visibility platform?",
            answer:
              "Both. OuraTrust combines a reputation & reviews pillar with a local visibility pillar to give businesses a more complete view of local presence.",
          },
          {
            question: "What is included in Starter?",
            answer:
              "Starter covers review and reputation management: centralization, AI-assisted replies, QR code collection and a reputation dashboard. The full local visibility suite is not included in that plan.",
          },
          {
            question: "What does Pro add compared with Starter?",
            answer:
              "Pro adds the local visibility suite: GBP Audit, Local Visibility and Local Competitors, on top of the reputation & reviews foundation.",
          },
          {
            question: "Does local visibility mean exact Google rankings?",
            answer:
              "No. OuraTrust uses an honest product frame: it is a standardized measure of local presence, depending on source availability and quality. The goal is credible local guidance, not a misleading promise.",
          },
          {
            question: "Which review platforms are supported?",
            answer:
              "OuraTrust currently centralizes Google, Facebook, Yelp, TripAdvisor and Trustpilot so teams can manage multi-platform reputation from one interface.",
          },
          {
            question: "Does the AI automatically publish every reply?",
            answer:
              "OuraTrust favors useful but controlled AI. The platform generates drafts to save time while keeping human validation as the main trust framework.",
          },
          {
            question: "How do you handle the reliability of local SEO data?",
            answer:
              "We distinguish between real measures, unavailable data and historical states so the product does not present uncertain information as if it were guaranteed. Clarity of interpretation comes first.",
          },
          {
            question: "Is Business designed for multi-location operations?",
            answer:
              "Yes. Business is built for networks, groups and multi-location brands that need a centralized cockpit, stronger history and more advanced operating visibility across locations.",
          },
        ],
      },
      finalCta: {
        title: "Give your business a true local operating cockpit",
        subtitle:
          "Start with reputation in Starter, move to Pro for local visibility, or structure several locations with Business.",
        primaryCta: "Create account",
        secondaryCta: "Compare plans",
      },
    },
  };

  const page = content[language];
  const proofIcons = [ShieldCheck, Search, QrCode];
  const whyIcons = [CheckCircle2, Sparkles, Network];
  const visibilityIcons = [Search, LineChart, Building2];
  const reputationIcons = [Globe, Bot, BarChart3];
  const collectionIcons = [QrCode, Printer, MessageSquareHeart];
  const workflowIcons = [Store, MapPinned, MessageSquareHeart, ArrowRight];
  const useCaseIcons = [Store, Sparkles, Building2];

  return (
    <div className="min-h-screen bg-background">
      <SEOHead page="home" />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: page.faq.items.map((item) => ({
              "@type": "Question",
              name: item.question,
              acceptedAnswer: {
                "@type": "Answer",
                text: item.answer,
              },
            })),
          })}
        </script>
      </Helmet>

      <SocialProofToast />

      <header className="fixed top-0 left-0 right-0 z-50 glass-nav">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img src={logoOuraTrust} alt="OuraTrust" className="h-6 object-contain" width={158} height={24} />
          </Link>

          <div className="hidden sm:flex items-center gap-2 md:gap-4">
            <LanguageSwitcher />
            <Link to="/blog">
              <Button variant="ghost" className="text-muted-foreground">
                <BookOpen size={16} />
                {t.blog}
              </Button>
            </Link>
            <Link to="/demo">
              <Button variant="ghost" className="text-muted-foreground">
                {t.demo}
              </Button>
            </Link>
            <Link to="/login">
              <Button variant="ghost" className="text-muted-foreground">
                {t.login}
              </Button>
            </Link>
            <Link to="/signup">
              <Button className="shadow-button">{t.signup}</Button>
            </Link>
          </div>

          <div className="flex sm:hidden items-center gap-2">
            <LanguageSwitcher />
            <Button variant="ghost" size="icon" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Menu">
              {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </Button>
          </div>
        </div>

        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="sm:hidden bg-background/95 backdrop-blur-md border-t border-border"
            >
              <div className="container mx-auto px-4 py-4 flex flex-col gap-2">
                <Link to="/blog" onClick={() => setMobileMenuOpen(false)}>
                  <Button variant="ghost" className="w-full justify-start text-muted-foreground">
                    <BookOpen size={16} className="mr-2" />
                    {t.blog}
                  </Button>
                </Link>
                <Link to="/demo" onClick={() => setMobileMenuOpen(false)}>
                  <Button variant="ghost" className="w-full justify-start text-muted-foreground">
                    {t.demo}
                  </Button>
                </Link>
                <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                  <Button variant="ghost" className="w-full justify-start text-muted-foreground">
                    {t.login}
                  </Button>
                </Link>
                <Link to="/signup" onClick={() => setMobileMenuOpen(false)}>
                  <Button className="w-full shadow-button">{t.signup}</Button>
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      <main>
        <section className="relative overflow-hidden border-b border-border bg-gradient-to-b from-background via-muted/30 to-background pt-32 pb-20 md:pt-36 md:pb-24">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute left-1/2 top-0 h-80 w-80 -translate-x-1/2 rounded-full bg-primary/10 blur-3xl" />
            <div className="absolute right-0 top-24 h-72 w-72 rounded-full bg-accent/10 blur-3xl" />
          </div>
          <div className="container relative mx-auto px-4">
            <div className="grid gap-14 lg:grid-cols-[1.05fr_0.95fr] lg:items-center">
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="max-w-2xl"
              >
                <Badge variant="secondary" className="mb-6 rounded-full px-4 py-1.5 text-xs font-medium">
                  {page.hero.eyebrow}
                </Badge>
                <h1 className="max-w-3xl text-4xl font-semibold tracking-tight text-foreground md:text-6xl md:leading-[1.02]">
                  {page.hero.title}
                </h1>
                <p className="mt-6 max-w-2xl text-lg leading-8 text-muted-foreground md:text-xl">
                  {page.hero.subtitle}
                </p>

                <div className="mt-8 flex flex-wrap gap-3">
                  {page.hero.trustSignals.map((signal) => (
                    <div key={signal} className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-sm text-foreground shadow-sm">
                      <CheckCircle2 className="h-4 w-4 text-accent" />
                      <span>{signal}</span>
                    </div>
                  ))}
                </div>

                <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                  <Button asChild size="lg" className="btn-premium h-13 px-7 text-base">
                    <Link to="/signup?plan=pro">
                      {page.hero.primaryCta}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                  <Button asChild size="lg" variant="outline" className="h-13 px-7 text-base">
                    <Link to="/demo">{page.hero.secondaryCta}</Link>
                  </Button>
                </div>

                <p className="mt-4 text-sm text-muted-foreground">{page.hero.support}</p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.15 }}
                className="relative"
              >
                <div className="absolute -inset-4 rounded-[2rem] bg-primary/10 blur-3xl" />
                <div className="relative overflow-hidden rounded-[2rem] border border-border/60 bg-card p-3 shadow-2xl shadow-primary/10">
                  <img
                    src={dashboardScreenshot}
                    alt={page.hero.imageAlt}
                    className="h-full w-full rounded-[1.35rem] border border-border/60 object-cover"
                    loading="eager"
                  />
                  <div className="pointer-events-none absolute left-6 top-6 max-w-[13rem] rounded-2xl border border-border/60 bg-background/92 p-4 shadow-lg backdrop-blur">
                    <p className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">
                      {language === "fr" ? "Pilotage local" : "Local operating view"}
                    </p>
                    <p className="mt-2 text-sm font-medium leading-6 text-foreground">
                      {language === "fr"
                        ? "Réputation, fiche Google et visibilité locale enfin traitées dans une même lecture."
                        : "Reputation, Google profile and local visibility finally handled in one coherent frame."}
                    </p>
                  </div>
                  <div className="pointer-events-none absolute bottom-6 right-6 rounded-2xl border border-border/60 bg-background/92 px-4 py-3 shadow-lg backdrop-blur">
                    <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
                      {language === "fr" ? "Pro" : "Pro"}
                    </p>
                    <p className="mt-1 text-sm font-medium text-foreground">
                      {language === "fr" ? "Inclut la suite Visibilité locale" : "Includes the Local Visibility suite"}
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        <TrustBar />

        <section className="py-20 md:py-24">
          <div className="container mx-auto px-4">
            <div className="mx-auto max-w-3xl text-center">
              <Badge variant="outline" className="rounded-full px-4 py-1.5 text-xs font-medium">
                {page.proof.eyebrow}
              </Badge>
              <h2 className="mt-5 text-3xl font-semibold tracking-tight text-foreground md:text-5xl">
                {page.proof.title}
              </h2>
              <p className="mt-4 text-lg leading-8 text-muted-foreground">{page.proof.subtitle}</p>
            </div>

            <div className="mt-12 grid gap-6 lg:grid-cols-3">
              {page.proof.items.map((item, index) => {
                const Icon = proofIcons[index];
                return (
                  <motion.div
                    key={item.title}
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.45, delay: index * 0.08 }}
                    className="rounded-3xl border border-border bg-card p-8 shadow-sm"
                  >
                    <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10">
                      <Icon className="h-5 w-5 text-primary" />
                    </div>
                    <h3 className="mt-6 text-xl font-semibold text-foreground">{item.title}</h3>
                    <p className="mt-3 text-base leading-7 text-muted-foreground">{item.description}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        <section className="border-y border-border bg-muted/30 py-20 md:py-24">
          <div className="container mx-auto px-4">
            <div className="mx-auto max-w-3xl text-center">
              <Badge variant="outline" className="rounded-full px-4 py-1.5 text-xs font-medium">
                {page.why.eyebrow}
              </Badge>
              <h2 className="mt-5 text-3xl font-semibold tracking-tight text-foreground md:text-5xl">
                {page.why.title}
              </h2>
              <p className="mt-4 text-lg leading-8 text-muted-foreground">{page.why.subtitle}</p>
            </div>

            <div className="mt-12 grid gap-6 lg:grid-cols-3">
              {page.why.items.map((item, index) => {
                const Icon = whyIcons[index];
                return (
                  <motion.div
                    key={item.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.45, delay: index * 0.08 }}
                    className="rounded-3xl border border-border bg-background p-8 shadow-sm"
                  >
                    <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-accent/10">
                      <Icon className="h-5 w-5 text-accent" />
                    </div>
                    <h3 className="mt-6 text-xl font-semibold text-foreground">{item.title}</h3>
                    <p className="mt-3 text-base leading-7 text-muted-foreground">{item.description}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        <section className="py-20 md:py-24">
          <div className="container mx-auto px-4">
            <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-start">
              <div className="max-w-xl">
                <Badge className="rounded-full px-4 py-1.5 text-xs font-medium">{page.visibility.eyebrow}</Badge>
                <h2 className="mt-5 text-3xl font-semibold tracking-tight text-foreground md:text-5xl">
                  {page.visibility.title}
                </h2>
                <p className="mt-4 text-lg leading-8 text-muted-foreground">{page.visibility.subtitle}</p>
                <div className="mt-6 rounded-2xl border border-border bg-muted/60 p-5 text-sm leading-7 text-muted-foreground">
                  <span className="font-medium text-foreground">{language === "fr" ? "Important : " : "Important: "}</span>
                  {page.visibility.note}
                </div>
              </div>

              <div className="grid gap-6 md:grid-cols-3">
                {page.visibility.features.map((feature, index) => {
                  const Icon = visibilityIcons[index];
                  return (
                    <motion.div
                      key={feature.title}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.45, delay: index * 0.08 }}
                      className="rounded-3xl border border-border bg-card p-7 shadow-sm"
                    >
                      <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10">
                        <Icon className="h-5 w-5 text-primary" />
                      </div>
                      <h3 className="mt-6 text-xl font-semibold text-foreground">{feature.title}</h3>
                      <p className="mt-3 text-base leading-7 text-muted-foreground">{feature.description}</p>
                      <ul className="mt-5 space-y-3">
                        {feature.bullets.map((bullet) => (
                          <li key={bullet} className="flex items-center gap-3 text-sm text-foreground">
                            <CheckCircle2 className="h-4 w-4 text-accent" />
                            <span>{bullet}</span>
                          </li>
                        ))}
                      </ul>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        <section className="border-y border-border bg-muted/30 py-20 md:py-24">
          <div className="container mx-auto px-4">
            <div className="grid gap-10 lg:grid-cols-[0.92fr_1.08fr] lg:items-start">
              <div className="max-w-xl">
                <Badge variant="secondary" className="rounded-full px-4 py-1.5 text-xs font-medium">
                  {page.reputation.eyebrow}
                </Badge>
                <h2 className="mt-5 text-3xl font-semibold tracking-tight text-foreground md:text-5xl">
                  {page.reputation.title}
                </h2>
                <p className="mt-4 text-lg leading-8 text-muted-foreground">{page.reputation.subtitle}</p>
              </div>

              <div className="grid gap-6 md:grid-cols-3">
                {page.reputation.features.map((feature, index) => {
                  const Icon = reputationIcons[index];
                  return (
                    <motion.div
                      key={feature.title}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.45, delay: index * 0.08 }}
                      className="rounded-3xl border border-border bg-background p-7 shadow-sm"
                    >
                      <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-accent/10">
                        <Icon className="h-5 w-5 text-accent" />
                      </div>
                      <h3 className="mt-6 text-xl font-semibold text-foreground">{feature.title}</h3>
                      <p className="mt-3 text-base leading-7 text-muted-foreground">{feature.description}</p>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 md:py-24">
          <div className="container mx-auto px-4">
            <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-start">
              <div className="max-w-xl">
                <Badge variant="outline" className="rounded-full px-4 py-1.5 text-xs font-medium">
                  {page.collection.eyebrow}
                </Badge>
                <h2 className="mt-5 text-3xl font-semibold tracking-tight text-foreground md:text-5xl">
                  {page.collection.title}
                </h2>
                <p className="mt-4 text-lg leading-8 text-muted-foreground">{page.collection.subtitle}</p>
              </div>

              <div className="grid gap-6 md:grid-cols-3">
                {page.collection.features.map((feature, index) => {
                  const Icon = collectionIcons[index];
                  return (
                    <motion.div
                      key={feature.title}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.45, delay: index * 0.08 }}
                      className="rounded-3xl border border-border bg-card p-7 shadow-sm"
                    >
                      <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10">
                        <Icon className="h-5 w-5 text-primary" />
                      </div>
                      <h3 className="mt-6 text-xl font-semibold text-foreground">{feature.title}</h3>
                      <p className="mt-3 text-base leading-7 text-muted-foreground">{feature.description}</p>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        <section className="border-y border-border bg-muted/30 py-20 md:py-24">
          <div className="container mx-auto px-4">
            <div className="mx-auto max-w-3xl text-center">
              <Badge variant="secondary" className="rounded-full px-4 py-1.5 text-xs font-medium">
                {page.workflow.eyebrow}
              </Badge>
              <h2 className="mt-5 text-3xl font-semibold tracking-tight text-foreground md:text-5xl">
                {page.workflow.title}
              </h2>
              <p className="mt-4 text-lg leading-8 text-muted-foreground">{page.workflow.subtitle}</p>
            </div>

            <div className="mt-12 grid gap-6 lg:grid-cols-4">
              {page.workflow.steps.map((step, index) => {
                const Icon = workflowIcons[index];
                return (
                  <motion.div
                    key={step.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.45, delay: index * 0.08 }}
                    className="relative rounded-3xl border border-border bg-background p-7 shadow-sm"
                  >
                    <div className="mb-6 flex items-center justify-between">
                      <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-accent/10">
                        <Icon className="h-5 w-5 text-accent" />
                      </div>
                      <span className="text-sm font-medium text-muted-foreground">0{index + 1}</span>
                    </div>
                    <h3 className="text-xl font-semibold text-foreground">{step.title}</h3>
                    <p className="mt-3 text-base leading-7 text-muted-foreground">{step.description}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        <section className="py-20 md:py-24">
          <div className="container mx-auto px-4">
            <div className="mx-auto max-w-3xl text-center">
              <Badge variant="outline" className="rounded-full px-4 py-1.5 text-xs font-medium">
                {page.useCases.eyebrow}
              </Badge>
              <h2 className="mt-5 text-3xl font-semibold tracking-tight text-foreground md:text-5xl">
                {page.useCases.title}
              </h2>
              <p className="mt-4 text-lg leading-8 text-muted-foreground">{page.useCases.subtitle}</p>
            </div>

            <div className="mt-12 grid gap-6 lg:grid-cols-3">
              {page.useCases.items.map((item, index) => {
                const Icon = useCaseIcons[index];
                return (
                  <motion.div
                    key={item.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.45, delay: index * 0.08 }}
                    className="rounded-3xl border border-border bg-card p-8 shadow-sm"
                  >
                    <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10">
                      <Icon className="h-5 w-5 text-primary" />
                    </div>
                    <div className="mt-6 flex items-center justify-between gap-4">
                      <h3 className="text-xl font-semibold text-foreground">{item.title}</h3>
                      <Badge variant="secondary" className="rounded-full px-3 py-1 text-xs font-medium">
                        {item.plan}
                      </Badge>
                    </div>
                    <p className="mt-3 text-base leading-7 text-muted-foreground">{item.description}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        <TestimonialsSection />
        <PricingSection />

        <section id="faq" className="border-y border-border bg-muted/30 py-20 md:py-24">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mx-auto max-w-3xl text-center"
            >
              <Badge variant="outline" className="rounded-full px-4 py-1.5 text-xs font-medium">
                FAQ
              </Badge>
              <h2 className="mt-5 text-3xl font-semibold tracking-tight text-foreground md:text-5xl">
                {page.faq.title}
              </h2>
              <p className="mt-4 text-lg leading-8 text-muted-foreground">{page.faq.subtitle}</p>
            </motion.div>

            <div className="mx-auto mt-12 max-w-3xl">
              <Accordion type="single" collapsible className="space-y-4">
                {page.faq.items.map((item, index) => (
                  <AccordionItem key={item.question} value={`faq-${index}`} className="rounded-2xl border border-border bg-card px-6">
                    <AccordionTrigger className="text-left text-base font-medium text-foreground hover:no-underline">
                      {item.question}
                    </AccordionTrigger>
                    <AccordionContent className="pb-6 text-base leading-7 text-muted-foreground">
                      {item.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </section>

        <section className="py-20 md:py-24">
          <div className="container mx-auto px-4">
            <div className="overflow-hidden rounded-[2rem] border border-border bg-gradient-to-br from-card via-card to-muted/60 p-8 shadow-xl md:p-12">
              <div className="mx-auto max-w-3xl text-center">
                <Badge className="rounded-full px-4 py-1.5 text-xs font-medium">
                  OuraTrust
                </Badge>
                <h2 className="mt-5 text-3xl font-semibold tracking-tight text-foreground md:text-5xl">
                  {page.finalCta.title}
                </h2>
                <p className="mt-4 text-lg leading-8 text-muted-foreground">{page.finalCta.subtitle}</p>
                <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
                  <Button asChild size="lg" className="btn-premium h-13 px-7 text-base">
                    <Link to="/signup?plan=pro">
                      {page.finalCta.primaryCta}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                  <Button asChild size="lg" variant="outline" className="h-13 px-7 text-base">
                    <Link to="#pricing">{page.finalCta.secondaryCta}</Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Index;
